import type { RecurringExpense } from '@/types'
import type { IRecurringExpenseRepository } from '@/repository/recurringExpenseTypes'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from './schema'

type ExpenseRow = Database['public']['Tables']['recurring_expenses']['Row']
type ExpenseInsert = Database['public']['Tables']['recurring_expenses']['Insert']

/**
 * Cloud-backed recurring-expense repository. Mirrors the local-storage
 * variant exactly so the SaaS swap is invisible to the rest of the app.
 *
 * `billing_months` is stored as `smallint[]` and round-trips as
 * `number[]` through the PostgREST JSON serializer — no manual mapping
 * needed beyond optional/null handling.
 */
export class SupabaseRecurringExpenseRepo implements IRecurringExpenseRepository {
  private readonly client: SupabaseClient<Database>
  private readonly userId: string
  private readonly workspaceId: string

  constructor(client: SupabaseClient<Database>, userId: string, workspaceId: string) {
    this.client = client
    this.userId = userId
    this.workspaceId = workspaceId
  }

  async getAll(): Promise<RecurringExpense[]> {
    const { data, error } = await this.client
      .from('recurring_expenses')
      .select('*')
      .eq('workspace_id', this.workspaceId)
      .order('created_at', { ascending: true })

    if (error) throw new Error(`[recurring_expenses.getAll] ${error.message}`)
    return (data ?? []).map(rowToExpense)
  }

  async getById(id: string): Promise<RecurringExpense | null> {
    const { data, error } = await this.client
      .from('recurring_expenses')
      .select('*')
      .eq('id', id)
      .eq('workspace_id', this.workspaceId)
      .maybeSingle()

    if (error) throw new Error(`[recurring_expenses.getById] ${error.message}`)
    return data ? rowToExpense(data) : null
  }

  async upsert(expense: RecurringExpense): Promise<RecurringExpense> {
    const row = expenseToInsert(expense, this.userId, this.workspaceId)
    const { data, error } = await this.client
      .from('recurring_expenses')
      .upsert(row, { onConflict: 'id' })
      .select()
      .single()

    if (error) throw new Error(`[recurring_expenses.upsert] ${error.message}`)
    return rowToExpense(data)
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.client
      .from('recurring_expenses')
      .delete()
      .eq('id', id)
      .eq('workspace_id', this.workspaceId)

    if (error) throw new Error(`[recurring_expenses.delete] ${error.message}`)
  }

  async bulkReplace(expenses: RecurringExpense[]): Promise<void> {
    const { error: delErr } = await this.client
      .from('recurring_expenses')
      .delete()
      .eq('workspace_id', this.workspaceId)
    if (delErr) throw new Error(`[recurring_expenses.bulkReplace:delete] ${delErr.message}`)

    if (expenses.length === 0) return

    const rows = expenses.map((e) => expenseToInsert(e, this.userId, this.workspaceId))
    const { error: insErr } = await this.client.from('recurring_expenses').insert(rows)
    if (insErr) throw new Error(`[recurring_expenses.bulkReplace:insert] ${insErr.message}`)
  }
}

function rowToExpense(row: ExpenseRow): RecurringExpense {
  const expense: RecurringExpense = {
    id: row.id,
    workspaceId: row.workspace_id,
    name: row.name,
    category: row.category,
    amount: Number(row.amount),
    frequency: row.frequency,
    isActive: row.is_active,
    isEssential: row.is_essential,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
  if (row.billing_months !== null) {
    expense.billingMonths = row.billing_months.map((n) => Number(n))
  }
  if (row.notes !== null) expense.notes = row.notes
  return expense
}

function expenseToInsert(
  expense: RecurringExpense,
  userId: string,
  workspaceId: string
): ExpenseInsert {
  return {
    id: expense.id,
    workspace_id: workspaceId,
    user_id: userId,
    name: expense.name,
    category: expense.category,
    amount: expense.amount,
    frequency: expense.frequency,
    billing_months: expense.billingMonths ?? null,
    is_active: expense.isActive,
    is_essential: expense.isEssential,
    notes: expense.notes ?? null,
    created_at: expense.createdAt,
    updated_at: expense.updatedAt,
  }
}
